// ─────────────────────────────────────────────
//  Chime — the new-message notification sound (D-03). One sound for all
//  message types, on by default, per-device mute (FLAG-4, localStorage).
//
//  Autoplay safety (Pitfall 6): browsers reject `audio.play()` before the
//  user's first gesture (and on Capacitor/iOS the audio context starts
//  suspended). So we gate playback behind a session "audio unlocked" flag set
//  on the first pointer/key interaction, and ALWAYS catch the play() rejection.
//  This module NEVER throws and NEVER prompts — a chime that can't play is
//  silently skipped (UI-SPEC).
// ─────────────────────────────────────────────

const MUTE_KEY = "atc.chime.muted";
const CHIME_SRC = "/chime.mp3";

let audioUnlocked = false; // flips true on the first user gesture
let audioEl = null; // single reused <audio> element
let audioCtx = null; // shared Web Audio context (emergency alarm)
let listenersBound = false;

function markUnlocked() {
  audioUnlocked = true;
}

// Shared Web Audio context — used to synthesize the emergency alarm so it needs
// NO bundled asset and works offline. Created lazily; null where unsupported.
function getAudioCtx() {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  try {
    if (!audioCtx) audioCtx = new AC();
  } catch {
    return null;
  }
  return audioCtx;
}

// Call once near app start. Idempotent. Sets the session "unlocked" flag on the
// first pointerdown/keydown/touchstart, then removes its own listeners. Safe in
// non-browser (test) environments — it no-ops if there's no window/document.
export function unlockAudioOnFirstGesture() {
  if (listenersBound) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  listenersBound = true;

  const onGesture = () => {
    markUnlocked();
    // Resume the Web Audio context (starts suspended on iOS/Capacitor) so the
    // first emergency alarm isn't swallowed. Never throws.
    try {
      const ctx = getAudioCtx();
      if (ctx && ctx.state === "suspended") ctx.resume();
    } catch {
      /* never throw */
    }
    // Best-effort warm-up: a muted no-op play primes the element on iOS so the
    // first real chime isn't swallowed. Never throws.
    try {
      const el = getAudio();
      if (el) {
        const prevMuted = el.muted;
        el.muted = true;
        const p = el.play();
        if (p && typeof p.then === "function") {
          p.then(() => {
            el.pause();
            el.currentTime = 0;
            el.muted = prevMuted;
          }).catch(() => {
            el.muted = prevMuted;
          });
        } else {
          el.muted = prevMuted;
        }
      }
    } catch {
      /* never throw */
    }
    document.removeEventListener("pointerdown", onGesture);
    document.removeEventListener("keydown", onGesture);
    document.removeEventListener("touchstart", onGesture);
  };

  document.addEventListener("pointerdown", onGesture, { once: false });
  document.addEventListener("keydown", onGesture, { once: false });
  document.addEventListener("touchstart", onGesture, { once: false });
}

function getAudio() {
  if (typeof Audio === "undefined") return null;
  if (!audioEl) {
    audioEl = new Audio(CHIME_SRC);
    audioEl.preload = "auto";
  }
  return audioEl;
}

// Read the per-device mute preference (FLAG-4). Defaults to UNMUTED.
export function isChimeMuted() {
  try {
    return window.localStorage.getItem(MUTE_KEY) === "true";
  } catch {
    return false;
  }
}

// Persist the per-device mute preference.
export function setChimeMuted(muted) {
  try {
    window.localStorage.setItem(MUTE_KEY, muted ? "true" : "false");
  } catch {
    /* localStorage unavailable — ignore */
  }
}

// Play the chime. Returns early (silently) when audio is still locked (no
// gesture yet) or when muted. The caller may pass an explicit `muted` flag;
// when omitted it reads localStorage. ALWAYS swallows the play() rejection
// (NotAllowedError) — never throws, never prompts (Pitfall 6).
export function playChime(muted) {
  const isMuted = muted === undefined ? isChimeMuted() : !!muted;
  if (isMuted || !audioUnlocked) return;
  const el = getAudio();
  if (!el) return;
  try {
    el.currentTime = 0;
    el.muted = false;
    const p = el.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  } catch {
    /* never throw */
  }
}

// Play the EMERGENCY alarm — a loud, urgent two-tone siren that lasts a few
// seconds (default ~3s), aimed at phones (pairs with a long vibration in
// AlertContext). Synthesized via Web Audio so it needs no bundled asset and
// works offline. UNLIKE the message chime this ignores the chime-mute toggle —
// an emergency is a safety alarm — but it still respects the browser autoplay
// gate (audioUnlocked): with no prior gesture the browser blocks all audio and
// there is nothing we can do. ALWAYS swallows errors — never throws.
export function playEmergencyAlarm(durationMs = 3000) {
  if (!audioUnlocked) return;
  const ctx = getAudioCtx();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") ctx.resume();
    const beep = 0.25; // tone length (s)
    const gap = 0.12; // silence between tones (s)
    const period = beep + gap;
    const count = Math.max(1, Math.round(durationMs / 1000 / period));
    const start = ctx.currentTime + 0.01;
    for (let i = 0; i < count; i++) {
      const t0 = start + i * period;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      // Alternate high/low like an alarm/siren.
      osc.frequency.setValueAtTime(i % 2 === 0 ? 880 : 660, t0);
      // Quick attack / decay envelope so each beep is crisp (no clicks).
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.5, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + beep);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + beep);
    }
  } catch {
    /* never throw */
  }
}

// Play the ⚡ URGENCY chirp — shorter and higher than the emergency siren so
// the two are distinguishable by ear (~1.4s of fast two-tone chirps). Like the
// emergency alarm it IGNORES the chime-mute toggle (a summons to the room is a
// clinical signal, not a message chime) but still respects the browser
// autoplay gate (audioUnlocked). ALWAYS swallows errors — never throws.
// Vibration pairs with this in native/urgencyAlarm.js (Capacitor Haptics).
export function playUrgencyAlarm(durationMs = 1400) {
  if (!audioUnlocked) return;
  const ctx = getAudioCtx();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") ctx.resume();
    const beep = 0.14; // tone length (s) — faster than the emergency siren
    const gap = 0.08;
    const period = beep + gap;
    const count = Math.max(1, Math.round(durationMs / 1000 / period));
    const start = ctx.currentTime + 0.01;
    for (let i = 0; i < count; i++) {
      const t0 = start + i * period;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      osc.frequency.setValueAtTime(i % 2 === 0 ? 1175 : 880, t0);
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.45, t0 + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + beep);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + beep);
    }
  } catch {
    /* never throw */
  }
}

// Test seam: reset module state between tests.
export function __resetChimeForTests() {
  audioUnlocked = false;
  audioEl = null;
  audioCtx = null;
  listenersBound = false;
}
